/**
 * Next Step Engine
 *
 * A deterministic rule engine that computes exactly ONE recommended next action
 * based on the current system state. The engine evaluates rules in order and
 * returns the first matching rule's action.
 *
 * Design Principles:
 * 1. Exactly one CTA is selected at all times
 * 2. Rules are evaluated in priority order (first match wins)
 * 3. Each rule maps to a specific DEGAP phase and session type when applicable
 * 4. The CTA must link to a specific place, not a generic page
 * 5. Stage visibility: the six stages must be surfaced in order; an incomplete
 *    upstream stage is always the next step before any downstream stage (Doc 08 §6A)
 */

import type { DegapPhase } from '../types/prioritization/degapCycle';
import type { SessionType } from '../types/prioritization/session';
import type { SystemMaturity, MaturityStats } from '../hooks/prioritization/useSystemMaturity';
import type { StageCompletionStatus } from '../types/prioritization/context';
import { STAGE_ORDER, STAGE_LABELS, STAGE_DESCRIPTIONS, getNextIncompleteStage } from '../types/prioritization/context';

// ============================================================================
// Types
// ============================================================================

/**
 * Represents a single recommended next step
 */
export interface NextStep {
  /** Unique identifier for this step */
  id: string;
  
  /** Short action title (the CTA text) */
  title: string;
  
  /** One-sentence description of why this matters */
  description: string;
  
  /** Which DEGAP phase this advances (if applicable) */
  phase?: DegapPhase;
  
  /** Which session type to create/resume (if applicable) */
  sessionType?: SessionType;
  
  /** Navigation action */
  action: {
    type: 'navigate' | 'modal';
    target: string;
  };
  
  /** Priority level for visual treatment */
  priority: 'primary' | 'secondary';
  
  /** Icon name (from lucide-react) */
  icon?: string;
}

/**
 * System state used to evaluate rules
 */
export interface SystemState {
  maturity: SystemMaturity;
  stats: MaturityStats;
  
  // Cycle state
  hasCycles: boolean;
  hasActiveCycle: boolean;
  activeCycleId?: string;
  activeCycleName?: string;
  cycleStatus?: 'draft' | 'active' | 'paused' | 'completed' | 'archived';
  
  // Phase state
  currentPhase?: DegapPhase;
  phaseStatuses?: {
    DECIDE: 'not_started' | 'in_progress' | 'complete';
    ENGAGE: 'not_started' | 'in_progress' | 'complete';
    GATHER: 'not_started' | 'in_progress' | 'complete';
    ARRANGE: 'not_started' | 'in_progress' | 'complete';
    PRIORITIZE: 'not_started' | 'in_progress' | 'complete';
  };
  
  // Config session state
  hasConfigSession: boolean;
  configSessionLocked: boolean;
  configSessionId?: string;
  
  // Gather state
  hasItems: boolean;
  itemCount: number;
  shapedItemPercent: number;
  readinessThreshold: number;
  
  // Session state
  hasActiveSessions: boolean;
  activeSessionId?: string;
  hasCompletedRanking: boolean;
  
  // Focus list state
  hasFocusLists: boolean;
  hasProposedFocusList: boolean;
  
  // Priority list state
  hasPrioritySet: boolean;
  prioritySetStatus?: 'proposed' | 'decided' | 'ratified' | 'published' | 'superseded';
}

/**
 * A rule in the decision table
 */
interface DecisionRule {
  id: string;
  condition: (state: SystemState) => boolean;
  nextStep: NextStep;
}

// ============================================================================
// Decision Rules (Ordered - First Match Wins)
// ============================================================================

const DECISION_RULES: DecisionRule[] = [
  // Rule 0: No items exist - get items first before cycles
  {
    id: 'submit-first-item',
    condition: (state) => state.itemCount === 0,
    nextStep: {
      id: 'submit-first-item',
      title: 'Submit Your First Item',
      description: 'Add something to prioritize - an idea, feature, or work item.',
      action: { type: 'navigate', target: '/prioritization/intake/submit' },
      priority: 'primary',
      icon: 'Plus',
    },
  },
  
  // Rule 1: Has items but no cycles exist
  {
    id: 'create-cycle',
    condition: (state) => !state.hasCycles && state.itemCount > 0,
    nextStep: {
      id: 'create-cycle',
      title: 'Create Your First Cycle',
      description: 'A cycle defines what you\'re prioritizing and over what time horizon.',
      action: { type: 'navigate', target: '/prioritization/cycles/new' },
      priority: 'primary',
      icon: 'RefreshCw',
    },
  },
  
  // Rule 2: Cycle in draft, DECIDE phase not complete
  {
    id: 'define-cycle-purpose',
    condition: (state) => 
      state.cycleStatus === 'draft' && 
      state.phaseStatuses?.DECIDE !== 'complete',
    nextStep: {
      id: 'define-cycle-purpose',
      title: 'Define Cycle Purpose',
      description: 'Clarify what this prioritization effort is for and who it serves.',
      phase: 'DECIDE',
      action: { type: 'navigate', target: '/prioritization/cycles/${state.activeCycleId}' },
      priority: 'primary',
      icon: 'Target',
    },
  },
  
  // Rule 3: DECIDE complete, ENGAGE not started
  {
    id: 'run-setup-session',
    condition: (state) => 
      state.phaseStatuses?.DECIDE === 'complete' && 
      state.phaseStatuses?.ENGAGE === 'not_started',
    nextStep: {
      id: 'run-setup-session',
      title: 'Run Setup Session',
      description: 'Configure criteria, hierarchy, and participants for prioritization.',
      phase: 'ENGAGE',
      sessionType: 'engage',
      action: { type: 'navigate', target: '/prioritization/cycles/engage' },
      priority: 'primary',
      icon: 'Settings',
    },
  },
  
  // Rule 4: ENGAGE in progress, config not locked
  {
    id: 'complete-setup-session',
    condition: (state) => 
      state.phaseStatuses?.ENGAGE === 'in_progress' && 
      state.hasConfigSession && 
      !state.configSessionLocked,
    nextStep: {
      id: 'complete-setup-session',
      title: 'Complete Setup Session',
      description: 'Lock configuration to proceed with prioritization.',
      phase: 'ENGAGE',
      sessionType: 'engage',
      action: { type: 'navigate', target: '/prioritization/settings/config' },
      priority: 'primary',
      icon: 'Lock',
    },
  },
  
  // Rule 5: ENGAGE complete, GATHER not started
  {
    id: 'start-shaping-session',
    condition: (state) => 
      state.phaseStatuses?.ENGAGE === 'complete' && 
      state.phaseStatuses?.GATHER === 'not_started',
    nextStep: {
      id: 'start-shaping-session',
      title: 'Start Item Work Session',
      description: 'Collect and shape items that will be prioritized.',
      phase: 'GATHER',
      sessionType: 'gather',
      action: { type: 'navigate', target: '/prioritization/gather' },
      priority: 'primary',
      icon: 'FileEdit',
    },
  },
  
  // Rule 6: GATHER in progress, items not ready
  {
    id: 'shape-items',
    condition: (state) => 
      state.phaseStatuses?.GATHER === 'in_progress' && 
      state.shapedItemPercent < state.readinessThreshold,
    nextStep: {
      id: 'shape-items',
      title: 'Shape Your Items',
      description: '${state.shapedItemPercent}% of items are ready. Get to ${state.readinessThreshold}% to proceed.',
      phase: 'GATHER',
      sessionType: 'gather',
      action: { type: 'navigate', target: '/prioritization/shape' },
      priority: 'primary',
      icon: 'Wrench',
    },
  },
  
  // Rule 7: GATHER complete, ARRANGE not started
  {
    id: 'run-ranking-session',
    condition: (state) => 
      state.phaseStatuses?.GATHER === 'complete' && 
      state.phaseStatuses?.ARRANGE === 'not_started',
    nextStep: {
      id: 'run-ranking-session',
      title: 'Run Ranking Session',
      description: 'Apply prioritization tools to rank your items.',
      phase: 'ARRANGE',
      sessionType: 'arrange',
      action: { type: 'navigate', target: '/prioritization/arrange' },
      priority: 'primary',
      icon: 'ArrowUpDown',
    },
  },
  
  // Rule 8: ARRANGE in progress, no ranking done
  {
    id: 'complete-ranking',
    condition: (state) => 
      state.phaseStatuses?.ARRANGE === 'in_progress' && 
      !state.hasCompletedRanking,
    nextStep: {
      id: 'complete-ranking',
      title: 'Complete Ranking',
      description: 'Run a prioritization tool to generate ranked results.',
      phase: 'ARRANGE',
      sessionType: 'arrange',
      action: { type: 'navigate', target: '/prioritization/arrange' },
      priority: 'primary',
      icon: 'ListOrdered',
    },
  },
  
  // Rule 9: Focus list proposed, no priority list
  {
    id: 'create-priority-set',
    condition: (state) => 
      state.hasProposedFocusList && 
      !state.hasPrioritySet,
    nextStep: {
      id: 'create-priority-set',
      title: 'Create Priority List',
      description: 'Bundle your Item Pool into a priority list for governance.',
      phase: 'PRIORITIZE',
      action: { type: 'navigate', target: '/prioritization/lists' },
      priority: 'primary',
      icon: 'Package',
    },
  },
  
  // Rule 10: Priority list proposed
  {
    id: 'request-decision',
    condition: (state) => 
      state.prioritySetStatus === 'proposed',
    nextStep: {
      id: 'request-decision',
      title: 'Request Decision',
      description: 'Submit priorities for leadership approval.',
      phase: 'PRIORITIZE',
      action: { type: 'navigate', target: '/prioritization/lists' },
      priority: 'primary',
      icon: 'CheckSquare',
    },
  },
  
  // Rule 11: Priority list decided
  {
    id: 'ratify-priorities',
    condition: (state) => 
      state.prioritySetStatus === 'decided',
    nextStep: {
      id: 'ratify-priorities',
      title: 'Ratify & Lock',
      description: 'Final approval locks priorities and enables tracking.',
      phase: 'PRIORITIZE',
      action: { type: 'navigate', target: '/prioritization/lists' },
      priority: 'primary',
      icon: 'Shield',
    },
  },
  
  // Rule 12: Priority list ratified
  {
    id: 'publish-priorities',
    condition: (state) => 
      state.prioritySetStatus === 'ratified',
    nextStep: {
      id: 'publish-priorities',
      title: 'Publish priorities',
      description: 'Communicate priorities to the organization.',
      phase: 'PRIORITIZE',
      action: { type: 'navigate', target: '/prioritization/lists' },
      priority: 'primary',
      icon: 'Send',
    },
  },
  
  // Rule 13: Fallback - all complete or unknown state
  {
    id: 'review-dashboard',
    condition: () => true, // Always matches as fallback
    nextStep: {
      id: 'review-dashboard',
      title: 'Review Dashboard',
      description: 'Monitor your priorities and check for drift signals.',
      action: { type: 'navigate', target: '/prioritization' },
      priority: 'secondary',
      icon: 'LayoutDashboard',
    },
  },
];

// ============================================================================
// Engine Functions
// ============================================================================

/**
 * Compute the next recommended step based on current system state.
 * Evaluates rules in order and returns the first match.
 */
export function computeNextStep(state: SystemState): NextStep {
  for (const rule of DECISION_RULES) {
    if (rule.condition(state)) {
      // Handle dynamic string interpolation in descriptions
      const nextStep = { ...rule.nextStep };
      
      // Replace dynamic targets
      if (typeof nextStep.action.target === 'string' && nextStep.action.target.includes('${')) {
        nextStep.action.target = nextStep.action.target
          .replace('${state.activeCycleId}', state.activeCycleId || '')
          .replace('${state.activeSessionId}', state.activeSessionId || '')
          .replace('${state.configSessionId}', state.configSessionId || '');
      }
      if (nextStep.description.includes('${')) {
        nextStep.description = nextStep.description
          .replace('${state.shapedItemPercent}', String(state.shapedItemPercent))
          .replace('${state.readinessThreshold}', String(state.readinessThreshold));
      }
      
      return nextStep;
    }
  }
  
  // Should never reach here due to fallback rule, but TypeScript needs this
  return DECISION_RULES[DECISION_RULES.length - 1].nextStep;
}

/**
 * Get all applicable next steps (for showing secondary options)
 * Returns primary step plus up to 2 secondary options
 */
export function getNextStepsWithSecondary(state: SystemState): {
  primary: NextStep;
  secondary: NextStep[];
} {
  const primary = computeNextStep(state);
  
  // Define secondary options based on maturity
  const secondaryOptions: NextStep[] = [];
  
  if (state.maturity === 'empty' || state.maturity === 'seeded') {
    // For new users, offer import and sample options
    if (state.hasCycles) {
      secondaryOptions.push({
        id: 'import-items',
        title: 'Import items',
        description: 'Bring work in from CSV or external tools.',
        action: { type: 'navigate', target: '/prioritization/import' },
        priority: 'secondary',
        icon: 'Upload',
      });
    }
    
    secondaryOptions.push({
      id: 'explore-sample',
      title: 'Explore Sample Data',
      description: 'See how the system works with example data.',
      action: { type: 'navigate', target: '/prioritization?demo=true' },
      priority: 'secondary',
      icon: 'Eye',
    });
  }
  
  return {
    primary,
    secondary: secondaryOptions.slice(0, 2), // Max 2 secondary options
  };
}

/**
 * Build a SystemState from the available stores/hooks
 * This is a convenience function for building the state object
 */
type PhaseStatus = NonNullable<SystemState['phaseStatuses']>['DECIDE'];

/** The fields of a cycle that buildSystemState reads. */
export interface SystemStateCycleInput {
  id?: string;
  name?: string;
  status?: SystemState['cycleStatus'];
  currentPhase?: DegapPhase;
  phaseStatuses?: Partial<Record<keyof NonNullable<SystemState['phaseStatuses']>, { status?: PhaseStatus }>>;
}

export function buildSystemState(params: {
  maturity: SystemMaturity;
  stats: MaturityStats;
  cycles?: SystemStateCycleInput[];
  activeCycle?: SystemStateCycleInput;
  configSession?: { id?: string; state?: string } | null;
  focusLists?: { status?: string }[];
  prioritySets?: { status?: SystemState['prioritySetStatus'] }[];
}): SystemState {
  const { maturity, stats, cycles = [], activeCycle, configSession, focusLists = [], prioritySets = [] } = params;
  
  const draftCycle = cycles.find(c => c.status === 'draft');
  const activeCycleData = activeCycle || cycles.find(c => c.status === 'active');
  
  const proposedFocusList = focusLists.find(fl => fl.status === 'proposed');
  const latestPrioritySet = prioritySets[0]; // Assuming sorted by date
  
  return {
    maturity,
    stats,
    
    hasCycles: cycles.length > 0,
    hasActiveCycle: !!activeCycleData,
    activeCycleId: activeCycleData?.id,
    activeCycleName: activeCycleData?.name,
    cycleStatus: activeCycleData?.status || draftCycle?.status,
    
    currentPhase: activeCycleData?.currentPhase,
    phaseStatuses: activeCycleData?.phaseStatuses ? {
      DECIDE: activeCycleData.phaseStatuses.DECIDE?.status || 'not_started',
      ENGAGE: activeCycleData.phaseStatuses.ENGAGE?.status || 'not_started',
      GATHER: activeCycleData.phaseStatuses.GATHER?.status || 'not_started',
      ARRANGE: activeCycleData.phaseStatuses.ARRANGE?.status || 'not_started',
      PRIORITIZE: activeCycleData.phaseStatuses.PRIORITIZE?.status || 'not_started',
    } : undefined,
    
    hasConfigSession: !!configSession,
    configSessionLocked: configSession?.state === 'locked',
    configSessionId: configSession?.id,
    
    hasItems: stats.itemCount > 0,
    itemCount: stats.itemCount,
    shapedItemPercent: stats.shapedItemPercent,
    readinessThreshold: 80, // Default threshold
    
    hasActiveSessions: stats.hasActiveSessions,
    activeSessionId: undefined, // Would need to be passed in
    hasCompletedRanking: false, // Would need to check sessions
    
    hasFocusLists: focusLists.length > 0,
    hasProposedFocusList: !!proposedFocusList,
    
    hasPrioritySet: prioritySets.length > 0,
    prioritySetStatus: latestPrioritySet?.status,
  };
}

// ============================================================================
// Stage Visibility — Six-Stage Next-Step Engine (Context-Aware)
// Doc 08 §6A: The engine must evaluate all six stage completion conditions.
// Incomplete upstream stages surface before downstream ones.
// Stage completion is server-authoritative — this computes the CTA from
// a server-returned StageCompletionStatus, not inferred from client state.
// ============================================================================

/**
 * Represents a next-step action derived from the six-stage visibility rule.
 * Used by context-aware contexts (cycle, group, personal) that may not have
 * gone through the DEGAP phase lifecycle.
 */
export interface StageNextStep extends NextStep {
  stage: keyof StageCompletionStatus;
  allStages: Array<{
    stage: keyof StageCompletionStatus;
    label: string;
    description: string;
    complete: boolean;
  }>;
}

/** Navigation targets for each stage */
const STAGE_NAV_TARGETS: Record<keyof StageCompletionStatus, (contextId?: string) => string> = {
  participation:      (id) => id ? `/prioritization/cycles/${id}/participation` : '/prioritization/cycles',
  pool_formation:     (id) => id ? `/prioritization/cycles/${id}/pool` : '/prioritization/cycles',
  criteria_selection: (id) => id ? `/prioritization/cycles/${id}/criteria` : '/prioritization/cycles',
  criteria_expression:(id) => id ? `/prioritization/cycles/${id}/criteria` : '/prioritization/cycles',
  item_scoring:       (id) => id ? `/prioritization/sessions/new?cycleId=${id}` : '/prioritization/sessions',
  final_ranking:      (id) => id ? `/prioritization/sessions/new?cycleId=${id}&type=force_ranking` : '/prioritization/sessions',
};

/**
 * Given server-returned stage completion status, compute the context-aware next step.
 * Returns null if all six stages are complete (cycle is done).
 *
 * This is the canonical next-step function for context-aware Decision Units.
 * It must receive server-computed stage data — never compute from client state alone.
 */
export function computeStageNextStep(
  stageStatus: StageCompletionStatus,
  contextId?: string,
  activeCycleId?: string
): StageNextStep | null {
  const nextStage = getNextIncompleteStage(stageStatus);
  if (!nextStage) return null; // All stages complete

  const refId = activeCycleId ?? contextId;

  return {
    id: `stage-${nextStage}`,
    title: `Complete ${STAGE_LABELS[nextStage]}`,
    description: STAGE_DESCRIPTIONS[nextStage],
    action: {
      type: 'navigate',
      target: STAGE_NAV_TARGETS[nextStage](refId),
    },
    priority: 'primary',
    icon: nextStage === 'final_ranking' ? 'Award' : 'ChevronRight',
    stage: nextStage,
    allStages: STAGE_ORDER.map((stage) => ({
      stage,
      label: STAGE_LABELS[stage],
      description: STAGE_DESCRIPTIONS[stage],
      complete: stageStatus[stage],
    })),
  };
}

/**
 * Evaluate stage completion from a server readiness result.
 * The server returns { valid, context_type, missing_inputs, blockers } from
 * evaluate_context_readiness(). This function maps that to a StageCompletionStatus.
 *
 * NOTE: The preferred path is to call evaluate_stage_completion(context_id) RPC
 * (when that is built). For now, this provides a safe client-side derivation from
 * the readiness check, which is still server-authoritative.
 */
export function deriveStageCompletionFromReadiness(
  missingInputs: string[]
): StageCompletionStatus {
  const missing = new Set(missingInputs.map((m) => m.split(':')[0].trim()));

  return {
    participation:      !missing.has('participation') && !missing.has('participants'),
    pool_formation:     !missing.has('pool') && !missing.has('pool_formation') && !missing.has('item_pool'),
    criteria_selection: !missing.has('criteria') && !missing.has('criteria_selection'),
    criteria_expression:!missing.has('criteria_expression') && !missing.has('weights') && !missing.has('scales'),
    item_scoring:       !missing.has('item_scoring') && !missing.has('values'),
    final_ranking:      !missing.has('final_ranking') && !missing.has('ranking'),
  };
}

export { STAGE_ORDER, STAGE_LABELS, STAGE_DESCRIPTIONS, getNextIncompleteStage };
export type { StageCompletionStatus };

export default computeNextStep;
